// Módulo Cotizaciones (§ 1.17, 2.3): lista, ficha con líneas, aceptar/rechazar,
// PDF (T-702) y envío por email (T-754).
import type { ChangeEvent, JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Download, Eraser, Eye, FileText, ImageIcon, Italic, List, Minus, Paperclip, Pencil, Plus, Save, Send, Trash2, Underline } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.ts";
import { useMe } from "../lib/auth.ts";
import { useAccountNames } from "../lib/lookups.ts";
import type { Row } from "../lib/resource.ts";
import { formatDate, formatMoney } from "../lib/format.ts";
import { Badge, Button, Card, EmptyState, Field, Input, Loading, Modal, Select } from "../components/ui.tsx";
import { AsyncCombobox } from "../components/AsyncCombobox.tsx";
import { ViewPortfolioButton } from "../components/PortfolioButton.tsx";
import { toast } from "../components/toast.tsx";

const STAGE_COLOR: Record<string, string> = { "Aceptada": "green", "Rechazada": "red", "Entregada": "blue", "Confirmada": "purple", "Borrador": "gray" };

/** Introducción por defecto (HTML). */
const DEFAULT_INTRO_HTML = `<p>En respuesta a su solicitud y en función de los requerimientos especificados, nos complace presentarle nuestra oferta técnico-económica para el desarrollo de ________________________.</p><p>Nuestro compromiso es proporcionar una solución que cumpla con los más altos estándares de calidad y eficiencia, ajustándose a sus necesidades específicas. Adjunto encontrará información completa de la oferta, en la cual se describen las especificaciones técnicas del servicio, los beneficios asociados y los costos estimados.</p><p>Esperamos que nuestra propuesta sea de su interés y quedamos atentos a cualquier consulta o ajuste que requiera.</p><p>Agradecemos su tiempo y consideración. Quedamos a su disposición y respuesta.</p>`;

/** "Quiénes Somos" por defecto (HTML). */
const DEFAULT_ABOUT_HTML = `<p>SACS GROUP es una organización dedicada a brindar soluciones y herramientas a nuestros clientes en la planificación, preparación y respuesta para la gestión integral de sus riesgos. <b>SALVAMOS VIDAS</b>: fundada en 2001, es la compañía líder del mercado en soluciones para la gestión integral del riesgo, atendiendo las emergencias más complejas de tipo industrial y desarrollando programas de análisis de riesgo en Colombia y Perú.</p>`;

/** "Objetivo" por defecto (HTML). */
const DEFAULT_OBJECTIVE_HTML = `<p>Fortalecer las competencias críticas del personal de respuesta a emergencias de ________________ y el desarrollo de habilidades tácticas y estratégicas en escenarios reales de alta complejidad.</p>
<p>Cumplirán algunos objetivos a través de nuestra propuesta de valor, como lo son:</p>
<p><b>Certificación internacional:</b> cada uno de los cursos los certifica a nivel internacional, por medio del departamento ESTI de Texas A&amp;M University (TEEX). Certificación Cuerpo Oficial de Bomberos.</p>
<p><b>Entrenamiento en Escenarios Reales:</b> ejecutar maniobras prácticas en simuladores de fuego vivo a gran escala y escenarios industriales especializados (cargaderos, plantas de procesos, tanques de almacenamiento), enfrentando condiciones operativas reales de calor y presión.</p>
<p><b>Estandarización bajo Normativas Globales:</b> alinear los procedimientos de respuesta de la compañía con marcos normativos reconocidos como la NFPA (para incendios y materiales peligrosos), OPITO y OMI (para operaciones offshore y portuarias).</p>
<p><b>Liderazgo y Gestión de Crisis:</b> desarrollar capacidades avanzadas en el Sistema de Comando de Incidentes (SCI) y gerencia de emergencias para optimizar la toma de decisiones estratégica y la coordinación de recursos en incidentes de gran magnitud.</p>
<p><b>Preparación en Nuevos Riesgos:</b> capacitar al personal en la gestión de riesgos emergentes, tales como sistemas de almacenamiento de energías renovables y respuesta específica ante fugas de cloro y amoníaco.</p>
<p><b>Especialización en respuesta a emergencias:</b> posibilidad de optar por la especialización de respuesta a emergencias de TEEX, en la que se deben cumplir los requisitos de diferentes módulos para ser certificado como "Especialista en respuesta a emergencias" a nivel internacional, dentro de un plazo de 10 años en la ejecución de todos los cursos o módulos que esta exige.</p>`;

/** "Alcance" por defecto (HTML). */
const DEFAULT_SCOPE_HTML = `<p>El alcance de este programa comprende desde la transferencia de conocimientos teóricos avanzados hasta la ejecución de maniobras prácticas en escenarios de alta complejidad, abarcando los siguientes componentes:</p>
<p><b>Formación Técnica Especializada:</b> la capacitación se centrará en las áreas críticas definidas, bajo estándares internacionales (NFPA, TEEX y/o PROBOARD cuando aplique).</p>
<p><b>Componente Práctico y Entrenamiento de Campo:</b> incluye el acceso y uso de simuladores de fuego vivo y escenarios de entrenamiento de última generación.</p>
<p><b>Certificación y Acreditación:</b> la expedición de certificados internacionales por parte de Texas A&amp;M Engineering Extension Service (TEEX).</p>
<p>Es importante mencionar que el alcance <b>NO incluye</b>: suministro de Equipo de Protección Personal (EPP) nivel estructural o industrial (cada participante debe portar su equipo propio según los requisitos de seguridad del curso).</p>`;

/** Texto plano antiguo → HTML; si ya es HTML se respeta; vacío → default. */
function introToHtml(raw: string): string {
  if (!raw.trim()) return DEFAULT_INTRO_HTML;
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return raw.split(/\n{2,}/).map((p) => `<p>${p.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>")}</p>`).join("");
}

/** Convierte a HTML para el editor (sin default): texto plano → párrafos. */
function toEditorHtml(raw: string): string {
  if (!raw.trim()) return "";
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return raw.split(/\n{2,}/).map((p) => `<p>${p.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>")}</p>`).join("");
}

/** Editor de texto enriquecido: formato, alineación, listas, pegar imágenes y
 *  redimensionarlas manteniendo su proporción. */
function RichTextEditor({ initialHtml, onChange, minHeight = 290 }: { initialHtml: string; onChange: (html: string) => void; minHeight?: number }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const selImg = useRef<HTMLImageElement | null>(null);
  const [hasImg, setHasImg] = useState(false);
  useEffect(() => { if (ref.current) ref.current.innerHTML = initialHtml; /* solo al montar */ }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const emit = (): void => onChange(ref.current?.innerHTML ?? "");
  const cmd = (command: string): void => { document.execCommand(command); ref.current?.focus(); emit(); };

  const selectImg = (img: HTMLImageElement | null): void => {
    ref.current?.querySelectorAll("img.rt-img-sel").forEach((i) => i.classList.remove("rt-img-sel"));
    selImg.current = img;
    if (img) img.classList.add("rt-img-sel");
    setHasImg(Boolean(img));
  };
  const onClick = (e: React.MouseEvent): void => {
    const t = e.target as HTMLElement;
    selectImg(t instanceof HTMLImageElement ? t : null);
  };
  const onPaste = (e: React.ClipboardEvent): void => {
    const items = e.clipboardData?.items; if (!items) return;
    for (const it of Array.from(items)) {
      if (it.type.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile(); if (!file) return;
        const r = new FileReader();
        r.onload = () => { document.execCommand("insertHTML", false, `<img src="${String(r.result)}" style="max-width:100%;height:auto;" />`); emit(); };
        r.readAsDataURL(file);
        return;
      }
    }
  };
  const resize = (factor: number): void => {
    const img = selImg.current; if (!img) return;
    const w = img.getBoundingClientRect().width || img.naturalWidth || 200;
    const maxW = ref.current?.clientWidth ?? 600;
    const nw = Math.max(40, Math.min(maxW, Math.round(w * factor)));
    img.style.width = `${nw}px`; img.style.height = "auto"; img.style.maxWidth = "100%";
    emit();
  };
  const clearAll = (): void => {
    if (!window.confirm("¿Borrar todo el contenido de este cuadro?")) return;
    if (ref.current) ref.current.innerHTML = "";
    selImg.current = null; setHasImg(false);
    emit(); ref.current?.focus();
  };

  const Btn = ({ onClick: on, title, children }: { onClick: () => void; title: string; children: JSX.Element }): JSX.Element => (
    <button type="button" title={title} aria-label={title} onMouseDown={(e) => e.preventDefault()} onClick={on}
      className="w-7 h-7 grid place-items-center rounded text-ink-secondary hover:bg-primary-soft hover:text-primary transition-colors">
      {children}
    </button>
  );
  return (
    <div className="border border-line rounded-lg overflow-hidden bg-surface">
      <div className="flex items-center gap-0.5 border-b border-line bg-canvas px-1.5 py-1 flex-wrap">
        <Btn onClick={() => cmd("bold")} title="Negrita"><Bold size={15} strokeWidth={2.25} /></Btn>
        <Btn onClick={() => cmd("italic")} title="Cursiva"><Italic size={15} strokeWidth={2.25} /></Btn>
        <Btn onClick={() => cmd("underline")} title="Subrayado"><Underline size={15} strokeWidth={2.25} /></Btn>
        <span className="w-px h-4 bg-line mx-1" />
        <Btn onClick={() => cmd("justifyLeft")} title="Alinear a la izquierda"><AlignLeft size={15} strokeWidth={2} /></Btn>
        <Btn onClick={() => cmd("justifyCenter")} title="Centrar"><AlignCenter size={15} strokeWidth={2} /></Btn>
        <Btn onClick={() => cmd("justifyRight")} title="Alinear a la derecha"><AlignRight size={15} strokeWidth={2} /></Btn>
        <Btn onClick={() => cmd("justifyFull")} title="Justificar"><AlignJustify size={15} strokeWidth={2} /></Btn>
        <span className="w-px h-4 bg-line mx-1" />
        <Btn onClick={() => cmd("insertUnorderedList")} title="Lista con viñetas"><List size={15} strokeWidth={2} /></Btn>
        <span className="w-px h-4 bg-line mx-1" />
        {hasImg ? (
          <span className="inline-flex items-center gap-0.5">
            <ImageIcon size={14} strokeWidth={2} className="text-primary mx-0.5" />
            <Btn onClick={() => resize(0.9)} title="Reducir imagen"><Minus size={15} strokeWidth={2.25} /></Btn>
            <Btn onClick={() => resize(1.1)} title="Ampliar imagen"><Plus size={15} strokeWidth={2.25} /></Btn>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-ink-muted px-1"><ImageIcon size={13} strokeWidth={2} /> Pega una imagen (Ctrl+V) y haz clic para ajustar tamaño</span>
        )}
        <button type="button" title="Limpiar todo" aria-label="Limpiar todo" onMouseDown={(e) => e.preventDefault()} onClick={clearAll}
          className="w-7 h-7 grid place-items-center rounded text-ink-secondary hover:bg-danger/10 hover:text-danger transition-colors ml-auto">
          <Eraser size={15} strokeWidth={2} />
        </button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit} onPaste={onPaste} onClick={onClick}
        className="rt-content px-3 py-2.5 text-sm text-ink outline-none overflow-y-auto leading-relaxed"
        style={{ minHeight }} />
    </div>
  );
}

export function QuoteList(): JSX.Element {
  const navigate = useNavigate();
  const accountNames = useAccountNames();
  const quotes = useQuery<{ rows: Row[] }>({ queryKey: ["list", "quotes"], queryFn: () => api.get("/quotes") });
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-ink">Cotizaciones</h1>
        <div className="flex gap-2">
          <ViewPortfolioButton />
          <Button variant="primary" onClick={() => setCreating(true)}>+ Nueva</Button>
        </div>
      </div>
      <Card>
        {quotes.isLoading ? <Loading /> : (quotes.data?.rows ?? []).length === 0 ? (
          <EmptyState title="Aún no hay cotizaciones" body="Crea una desde aquí o desde un negocio." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-canvas">
                <th className="th">Número</th><th className="th">Asunto</th><th className="th">Cuenta</th>
                <th className="th">Etapa</th><th className="th">Válida hasta</th>
              </tr></thead>
              <tbody>
                {(quotes.data?.rows ?? []).map((q) => (
                  <tr key={String(q["id"])} className="hover:bg-surface-hover cursor-pointer" onClick={() => navigate(`/quotes/${String(q["id"])}`)}>
                    <td className="td font-mono text-xs text-primary">{String(q["quoteNumber"])}</td>
                    <td className="td font-medium">{String(q["subject"])}</td>
                    <td className="td">{accountNames.get(String(q["accountId"])) ?? "—"}</td>
                    <td className="td"><Badge color={STAGE_COLOR[String(q["quoteStage"])] ?? "gray"}>{String(q["quoteStage"])}</Badge></td>
                    <td className="td">{formatDate(q["validUntil"] as string)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {creating && <QuoteFormModal onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); navigate(`/quotes/${id}`); }} />}
    </div>
  );
}

interface LineRow extends Row { _line: { lineDiscount: string; lineTax: string; lineTotal: string; lineNet: string } }

/** Botón de acción compacto (solo ícono, con tooltip). */
function IconBtn({ title, onClick, children }: { title: string; onClick: () => void; children: JSX.Element }): JSX.Element {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick}
      className="w-9 h-9 grid place-items-center rounded-lg border border-line bg-surface text-ink-secondary hover:bg-surface-hover hover:text-primary transition-colors">
      {children}
    </button>
  );
}

export function QuoteDetail(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const accountNames = useAccountNames();
  const me = useMe();
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);

  const quote = useQuery<{ quote: Row }>({ queryKey: ["record", "quotes", id], enabled: Boolean(id), queryFn: () => api.get(`/quotes/${id}`) });
  const attachments = useQuery<{ rows: Attach[] }>({ queryKey: ["quote-attachments", id], enabled: Boolean(id), queryFn: () => api.get(`/quotes/${id}/attachments`) });

  const invalidate = (): void => { void qc.invalidateQueries({ queryKey: ["record", "quotes", id] }); void qc.invalidateQueries({ queryKey: ["list", "quotes"] }); };
  const accept = useMutation({ mutationFn: () => api.post(`/quotes/${id}/accept`, { rejectOthers: false }), onSuccess: () => { toast.success("Cotización aceptada."); invalidate(); }, onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo aceptar.") });
  const reject = useMutation({ mutationFn: () => api.post(`/quotes/${id}/reject`), onSuccess: () => { toast.success("Cotización rechazada."); invalidate(); }, onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo rechazar.") });
  const reopen = useMutation({ mutationFn: () => api.patch(`/quotes/${id}`, { quoteStage: "Borrador" }), onSuccess: () => { toast.success("Cotización reabierta como borrador."); invalidate(); }, onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo reabrir.") });

  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const invalidateAtt = (): void => { void qc.invalidateQueries({ queryKey: ["quote-attachments", id] }); };
  const onPickFiles = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";  // permitir volver a elegir el mismo archivo
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const dataBase64 = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
          r.onerror = () => reject(new Error("read_error"));
          r.readAsDataURL(f);
        });
        await api.post(`/quotes/${id}/attachments`, { filename: f.name, contentType: f.type || "application/octet-stream", dataBase64 });
        ok++;
      } catch (err) {
        toast.error(err instanceof ApiError ? `${f.name}: ${err.message}` : `No se pudo subir ${f.name}.`);
      }
    }
    setUploading(false);
    if (ok) { toast.success(ok === 1 ? "Archivo adjuntado." : `${ok} archivos adjuntados.`); invalidateAtt(); }
  };

  if (quote.isLoading || !quote.data) return <Loading />;
  const q = quote.data.quote;
  const stage = String(q["quoteStage"]);
  const isFinal = stage === "Aceptada" || stage === "Rechazada";
  const readOnly = Boolean(me.data?.readOnly);
  const isOwner = Boolean(me.data?.id) && me.data?.id === q["ownerId"];
  const canEdit = !readOnly && (!isFinal || Boolean(me.data?.admin));
  const canReopen = isFinal && !readOnly && (Boolean(me.data?.admin) || isOwner);
  const attCount = attachments.data?.rows.length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button className="btn-ghost btn-sm" onClick={() => navigate("/quotes")}>←</button>
          <h1 className="text-xl font-bold text-ink">{String(q["subject"])}</h1>
          <Badge color={STAGE_COLOR[stage] ?? "gray"}>{stage}</Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <IconBtn title="Vista previa PDF" onClick={() => window.open(`/api/v1/quotes/${id}/pdf`, "_blank")}><Eye size={17} strokeWidth={1.75} /></IconBtn>
          <IconBtn title="Guardar PDF (descargar)" onClick={() => window.open(`/api/v1/quotes/${id}/pdf?download=1`, "_blank")}><Save size={17} strokeWidth={1.75} /></IconBtn>
          {canEdit && <IconBtn title="Editar" onClick={() => setEditing(true)}><Pencil size={17} strokeWidth={1.75} /></IconBtn>}
          {canEdit && (
            <div className="relative">
              <IconBtn title="Adjuntar archivos" onClick={() => fileInput.current?.click()}><Paperclip size={17} strokeWidth={1.75} /></IconBtn>
              {attCount > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] font-semibold flex items-center justify-center pointer-events-none">{attCount}</span>}
            </div>
          )}
          {!readOnly && <IconBtn title="Enviar por email" onClick={() => setSending(true)}><Send size={17} strokeWidth={1.75} /></IconBtn>}
          <input ref={fileInput} type="file" multiple className="hidden" onChange={onPickFiles} />
          {!isFinal && !readOnly && <Button variant="primary" onClick={() => accept.mutate()}>Marcar aceptada</Button>}
          {!isFinal && !readOnly && <Button variant="danger" onClick={() => reject.mutate()}>Rechazar</Button>}
          {canReopen && <Button onClick={() => reopen.mutate()} disabled={reopen.isPending}>Volver a borrador</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-ink-secondary mb-4">Información</h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><dt className="text-xs text-ink-muted">Número</dt><dd className="font-mono">{String(q["quoteNumber"])}</dd></div>
              <div><dt className="text-xs text-ink-muted">Cuenta</dt><dd>{accountNames.get(String(q["accountId"])) ?? "—"}</dd></div>
              <div><dt className="text-xs text-ink-muted">Válida hasta</dt><dd>{formatDate(q["validUntil"] as string)}</dd></div>
              <div><dt className="text-xs text-ink-muted">Total</dt><dd className="font-semibold">{formatMoney(q["grandTotal"] as string)}</dd></div>
            </dl>
          </Card>
        </div>
        <div>
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-ink-secondary mb-3">Total</h3>
            <div className="text-3xl font-bold text-ink">{formatMoney(q["grandTotal"] as string)}</div>
          </Card>
        </div>
      </div>

      <AttachmentsCard quoteId={String(id)} canEdit={canEdit} uploading={uploading} onAdd={() => fileInput.current?.click()} />

      <LinesCard quoteId={String(id)} editable={canEdit} />

      {sending && <SendEmailModal quoteId={String(id)} defaultSubject={`Cotización ${String(q["quoteNumber"])}`} onClose={() => setSending(false)} onSent={() => { setSending(false); invalidate(); }} />}
      {editing && <QuoteEditModal quote={q} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); invalidate(); }} />}
    </div>
  );
}

// ────────── Adjuntos de la cotización ──────────
function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

type Attach = { id: string; filename: string; contentType: string; sizeBytes: number; uploadedAt: string };

function AttachmentsCard({ quoteId, canEdit, uploading, onAdd }: { quoteId: string; canEdit: boolean; uploading: boolean; onAdd: () => void }): JSX.Element {
  const qc = useQueryClient();
  const list = useQuery<{ rows: Attach[] }>({ queryKey: ["quote-attachments", quoteId], queryFn: () => api.get(`/quotes/${quoteId}/attachments`) });
  const del = useMutation({
    mutationFn: (attId: string) => api.del(`/quotes/${quoteId}/attachments/${attId}`),
    onSuccess: () => { toast.success("Adjunto eliminado."); void qc.invalidateQueries({ queryKey: ["quote-attachments", quoteId] }); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo eliminar."),
  });
  const rows = list.data?.rows ?? [];

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ink-secondary">Adjuntos {rows.length > 0 && <span className="text-ink-muted font-normal">({rows.length})</span>}</h3>
        {canEdit && <Button variant="secondary" onClick={onAdd} disabled={uploading}><Paperclip size={15} strokeWidth={1.75} /> {uploading ? "Subiendo…" : "Adjuntar archivos"}</Button>}
      </div>
      {rows.length === 0
        ? <p className="text-sm text-ink-muted">Sin archivos adjuntos. {canEdit && "Estos se enviarán junto con la cotización por email."}</p>
        : (
          <ul className="divide-y divide-border">
            {rows.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-2.5">
                <FileText size={18} strokeWidth={1.75} className="text-ink-muted shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink truncate">{a.filename}</div>
                  <div className="text-xs text-ink-muted">{fmtBytes(a.sizeBytes)}</div>
                </div>
                <a className="btn-ghost btn-sm" href={`/api/v1/quotes/${quoteId}/attachments/${a.id}?download=1`} target="_blank" rel="noreferrer" title="Descargar"><Download size={16} strokeWidth={1.75} /></a>
                {canEdit && <button className="btn-ghost btn-sm text-danger" onClick={() => del.mutate(a.id)} disabled={del.isPending} title="Eliminar"><Trash2 size={16} strokeWidth={1.75} /></button>}
              </li>
            ))}
          </ul>
        )}
      {rows.length > 0 && canEdit && <p className="mt-3 text-xs text-ink-muted">Estos archivos se adjuntan automáticamente al enviar la cotización por email.</p>}
    </Card>
  );
}

// ────────── Líneas de la cotización (ver + editar/agregar/quitar) ──────────
function fmtQty(v: unknown): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v ?? "");
  return n % 1 === 0 ? String(n) : String(n);
}

function discountLabel(l: LineRow): string {
  const val = Number(l["discountValue"] ?? 0);
  if (!val) return "—";
  return l["discountType"] === "amount" ? formatMoney(String(val)) : `${fmtQty(val)}%`;
}

function LinesCard({ quoteId, editable }: { quoteId: string; editable: boolean }): JSX.Element {
  const qc = useQueryClient();
  const lines = useQuery<{ lines: LineRow[] }>({ queryKey: ["quote-lines", quoteId], queryFn: () => api.get(`/quotes/${quoteId}/line-items`) });
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const refresh = (): void => {
    void lines.refetch();
    void qc.invalidateQueries({ queryKey: ["record", "quotes", quoteId] });
    void qc.invalidateQueries({ queryKey: ["list", "quotes"] });
  };

  const del = (lineId: string): void => {
    if (!window.confirm("¿Quitar esta línea de la cotización?")) return;
    api.del(`/quotes/${quoteId}/line-items/${lineId}`)
      .then(() => { toast.success("Línea eliminada."); refresh(); })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : "No se pudo eliminar."));
  };

  const rows = lines.data?.lines ?? [];
  const cols = editable ? 6 : 5;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-ink-secondary">Líneas</h3>
        {editable && !adding && <Button small variant="primary" onClick={() => setAdding(true)}>+ Agregar línea</Button>}
      </div>
      {lines.isLoading ? <Loading /> : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-canvas">
              <th className="th">Descripción</th><th className="th">Cant.</th><th className="th">Precio</th><th className="th">Desc.</th><th className="th">Total</th>{editable && <th className="th"></th>}
            </tr></thead>
            <tbody>
              {rows.length === 0 && !adding ? (
                <tr><td className="td text-ink-muted" colSpan={cols}>Sin líneas.</td></tr>
              ) : rows.map((l) => (
                editId === String(l["id"]) ? (
                  <LineEditRow key={String(l["id"])} quoteId={quoteId} line={l} onDone={() => { setEditId(null); refresh(); }} onCancel={() => setEditId(null)} />
                ) : (
                  <tr key={String(l["id"])} className={editable ? "hover:bg-surface-hover" : ""}>
                    {(() => {
                      const edit = editable ? { onClick: () => setEditId(String(l["id"])), className: "td cursor-pointer", title: "Clic para editar" } : { className: "td" };
                      return (
                        <>
                          <td {...edit}>{String(l["lineDescription"] || l["productName"] || "—")}</td>
                          <td {...edit}>{fmtQty(l["quantity"])}</td>
                          <td {...edit}><span className={editable ? "hover:text-primary" : ""}>{formatMoney(String(l["listPrice"] ?? "0"))}</span></td>
                          <td {...edit}>{discountLabel(l)}</td>
                          <td className="td font-medium">{formatMoney(l._line.lineTotal)}</td>
                        </>
                      );
                    })()}
                    {editable && (
                      <td className="td whitespace-nowrap">
                        <button className="text-xs text-primary hover:underline" onClick={() => setEditId(String(l["id"]))}>Editar</button>
                        <button className="text-xs text-ink-muted hover:text-danger ml-3" onClick={() => del(String(l["id"]))}>Quitar</button>
                      </td>
                    )}
                  </tr>
                )
              ))}
              {adding && <LineAddRow quoteId={quoteId} onDone={() => { setAdding(false); refresh(); }} onCancel={() => setAdding(false)} />}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function LineAddRow({ quoteId, onDone, onCancel }: { quoteId: string; onDone: () => void; onCancel: () => void }): JSX.Element {
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const save = useMutation({
    mutationFn: () => api.post(`/quotes/${quoteId}/line-items`, { productId, quantity: Number(quantity) || 1 }),
    onSuccess: () => { toast.success("Línea agregada."); onDone(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo agregar."),
  });
  return (
    <tr className="bg-primary-soft/40">
      <td colSpan={6} className="td">
        <div className="flex items-end gap-3 flex-wrap py-1">
          <div className="flex-1 min-w-[320px]">
            <label className="block text-xs text-ink-muted mb-1">Servicio</label>
            <AsyncCombobox
              path="products"
              labelOf={(p) => `${String(p["productName"] ?? "")}${p["productCode"] ? ` · ${String(p["productCode"])}` : ""}`}
              value={productId}
              onChange={setProductId}
              placeholder="Buscar servicio…"
            />
          </div>
          <div className="w-28">
            <label className="block text-xs text-ink-muted mb-1">Cantidad</label>
            <Input value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>
          <div className="flex gap-2 pb-0.5">
            <Button small variant="primary" disabled={!productId || save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Guardando…" : "Guardar"}</Button>
            <Button small onClick={onCancel}>Cancelar</Button>
          </div>
          <div className="w-full text-xs text-ink-muted">Precio e IVA se toman del servicio (después editables en la línea).</div>
        </div>
      </td>
    </tr>
  );
}

function LineEditRow({ quoteId, line, onDone, onCancel }: { quoteId: string; line: LineRow; onDone: () => void; onCancel: () => void }): JSX.Element {
  const [desc, setDesc] = useState(String(line["lineDescription"] ?? ""));
  const [quantity, setQuantity] = useState(String(line["quantity"] ?? "1"));
  const [price, setPrice] = useState(String(line["listPrice"] ?? "0"));
  const [disc, setDisc] = useState(String(line["discountValue"] ?? "0"));
  const save = useMutation({
    mutationFn: () => api.patch(`/quotes/${quoteId}/line-items/${String(line["id"])}`, {
      lineDescription: desc, quantity: Number(quantity) || 1, listPrice: Number(price) || 0, discountValue: Number(disc) || 0,
    }),
    onSuccess: () => { toast.success("Línea actualizada."); onDone(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo actualizar."),
  });
  return (
    <tr className="bg-primary-soft/40">
      <td colSpan={6} className="td">
        <div className="flex items-end gap-3 flex-wrap py-1">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-xs text-ink-muted mb-1">Descripción</label>
            <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={String(line["productName"] ?? "Descripción")} />
          </div>
          <div className="w-24"><label className="block text-xs text-ink-muted mb-1">Cantidad</label><Input value={quantity} onChange={(e) => setQuantity(e.target.value)} /></div>
          <div className="w-36"><label className="block text-xs text-ink-muted mb-1">Precio</label><Input value={price} onChange={(e) => setPrice(e.target.value)} /></div>
          <div className="w-24"><label className="block text-xs text-ink-muted mb-1">Desc. %</label><Input value={disc} onChange={(e) => setDisc(e.target.value)} /></div>
          <div className="flex gap-2 pb-0.5">
            <Button small variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Guardando…" : "Guardar"}</Button>
            <Button small onClick={onCancel}>Cancelar</Button>
          </div>
        </div>
      </td>
    </tr>
  );
}

function toDateInput(v: unknown): string {
  if (!v) return "";
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function QuoteEditModal({ quote, onClose, onSaved }: { quote: Row; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [subject, setSubject] = useState(String(quote["subject"] ?? ""));
  const [headline, setHeadline] = useState(String(quote["headline"] ?? "Oferta Técnico-Económica"));
  const [ownerId, setOwnerId] = useState(String(quote["ownerId"] ?? ""));
  // Solo los "Desarrollador de negocios" pueden firmar la propuesta.
  const comerciales = useQuery<{ rows: Array<{ id: string; firstName: string; lastName: string; email: string }> }>({ queryKey: ["comerciales-firmantes"], queryFn: () => api.get("/comerciales?role=" + encodeURIComponent("Desarrollador de negocios")) });
  const [validUntil, setValidUntil] = useState(toDateInput(quote["validUntil"]));
  const [intro, setIntro] = useState(() => introToHtml(String(quote["intro"] ?? "")));
  const [about, setAbout] = useState(() => { const r = String(quote["about"] ?? ""); return r.trim() ? toEditorHtml(r) : DEFAULT_ABOUT_HTML; });
  const [objective, setObjective] = useState(() => { const r = String(quote["objective"] ?? ""); return r.trim() ? toEditorHtml(r) : DEFAULT_OBJECTIVE_HTML; });
  const [scope, setScope] = useState(() => { const r = String(quote["scope"] ?? ""); return r.trim() ? toEditorHtml(r) : DEFAULT_SCOPE_HTML; });
  const [terms, setTerms] = useState(() => toEditorHtml(String(quote["terms"] ?? "")));
  const [err, setErr] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (body: Row) => api.patch(`/quotes/${String(quote["id"])}`, body),
    onSuccess: () => { toast.success("Cotización actualizada."); onSaved(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });
  const submit = (): void => {
    if (!subject.trim()) { setErr({ subject: "Este campo es obligatorio." }); return; }
    const body: Row = { subject: subject.trim(), headline: headline.trim(), intro, about, objective, scope, terms };
    if (ownerId) body["ownerId"] = ownerId;
    if (validUntil) body["validUntil"] = validUntil;
    save.mutate(body);
  };
  return (
    <Modal open onClose={onClose} title="Editar cotización" wide
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit} disabled={save.isPending}>Guardar</Button></>}>
      <div className="space-y-4">
        <Field label="Encabezado del PDF" hint="Aparece arriba del título en el PDF (ej. Oferta Técnico-Económica)."><Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Oferta Técnico-Económica" /></Field>
        <Field label="Asunto (título)" required {...(err["subject"] ? { error: err["subject"] } : {})}><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        <Field label="Comercial (responsable / firma)" hint="Es quien firma la propuesta: su nombre, cargo, WhatsApp y correo salen en el PDF.">
          <Select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            {comerciales.data?.rows.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
          </Select>
        </Field>
        <Field label="Válida hasta"><Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></Field>
        <Field label="Introducción (carta de presentación)" hint="Aparece bajo el asunto en el PDF. Usa la barra para dar formato (negrita, cursiva, justificar…). Completa el espacio “____” con el objeto de la oferta.">
          <RichTextEditor initialHtml={intro} onChange={setIntro} />
        </Field>
        <Field label="Quiénes Somos" hint="Reseña de la empresa que aparece en la página 2 del PDF. Las tarjetas de unidades de negocio se toman de Configuración → Unidades de negocio.">
          <RichTextEditor initialHtml={about} onChange={setAbout} minHeight={200} />
        </Field>
        <Field label="Objetivo" hint="Aparece en su propia página del PDF, después de Quiénes Somos. Completa el espacio “____” con el cliente/alcance.">
          <RichTextEditor initialHtml={objective} onChange={setObjective} minHeight={240} />
        </Field>
        <Field label="Alcance" hint="Aparece en su propia página del PDF, después de Objetivo. Incluye lo que cubre y lo que NO incluye.">
          <RichTextEditor initialHtml={scope} onChange={setScope} minHeight={220} />
        </Field>
        <Field label="Términos y condiciones"><RichTextEditor initialHtml={terms} onChange={setTerms} minHeight={170} /></Field>
      </div>
    </Modal>
  );
}

function QuoteFormModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }): JSX.Element {
  const qc = useQueryClient();
  const [subject, setSubject] = useState("");
  const [accountId, setAccountId] = useState("");
  const [err, setErr] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: (body: Row) => api.post<{ quote: Row }>("/quotes", body),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["list", "quotes"] }); toast.success("Cotización creada."); onCreated(String(r.quote["id"])); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo crear."),
  });
  const submit = (): void => {
    const e: Record<string, string> = {};
    if (!subject.trim()) e["subject"] = "Este campo es obligatorio.";
    if (!accountId) e["accountId"] = "Este campo es obligatorio.";
    setErr(e);
    if (Object.keys(e).length > 0) return;
    create.mutate({ subject, accountId });
  };
  return (
    <Modal open onClose={onClose} title="Nueva cotización"
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit} disabled={create.isPending}>Guardar</Button></>}>
      <div className="space-y-4">
        <Field label="Asunto" required {...(err["subject"] ? { error: err["subject"] } : {})}><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        <Field label="Cuenta" required {...(err["accountId"] ? { error: err["accountId"] } : {})}>
          <AsyncCombobox
            path="accounts"
            labelOf={(a) => String(a["accountName"] ?? "")}
            value={accountId}
            onChange={(id) => setAccountId(id)}
            placeholder="Buscar cuenta…"
          />
        </Field>
      </div>
    </Modal>
  );
}

function SendEmailModal({ quoteId, defaultSubject, onClose, onSent }: { quoteId: string; defaultSubject: string; onClose: () => void; onSent: () => void }): JSX.Element {
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [bodyHtml, setBodyHtml] = useState("<p>Adjuntamos la cotización solicitada.</p>");
  const send = useMutation({
    mutationFn: (body: Row) => api.post(`/quotes/${quoteId}/send-email`, body),
    onSuccess: () => { toast.success("Cotización enviada (encolada)."); onSent(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo enviar."),
  });
  return (
    <Modal open onClose={onClose} title="Enviar cotización por email" wide
      footer={<><Button onClick={onClose}>Cancelar</Button>
        <Button variant="primary" disabled={!toEmail || send.isPending} onClick={() => send.mutate({ toEmail, subject, bodyHtml, attachPdf: true, moveToEntregada: true })}>Enviar</Button></>}>
      <div className="space-y-4">
        <Field label="Para" required><Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} /></Field>
        <Field label="Asunto" required><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        <Field label="Mensaje"><RichTextEditor initialHtml={bodyHtml} onChange={setBodyHtml} minHeight={150} /></Field>
        <label className="flex items-center gap-2 text-sm text-ink-secondary"><input type="checkbox" checked readOnly /> Adjuntar PDF y marcar como Entregada</label>
      </div>
    </Modal>
  );
}
